import CloseIcon from "@mui/icons-material/Close";
import { Box, CircularProgress, Container, Drawer, Stack } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Suspense, useContext, useEffect } from "react";
import MobilePreview from "../../../components/MobilePreview";
import CurrentUserContext from "../../../contexts/CurrentUserContext";
import {
  ReducerAction,
  useMobilePreviewDispatch,
} from "../../../contexts/MobilePreviewContext";
import { PreviewType } from "../../../contexts/MobilePreviewContext";
import nullthrows from "../../../utils/nullthrows";
import {
  BUILD_NAVIGATION,
  BUILD_THEME,
  ENGAGE_FEED_CREATE,
  ENGAGE_NOTIFICATIONS,
  SETTINGS_INTEGRATIONS,
} from "../../../utils/routes";
import NavigationTab from "../build_section/NavigationTab";
import ThemeTab from "../build_section/ThemeTab";
import FeedPostForm from "../engage/feed/FeedPostForm";
import NotificationForm from "../engage/notifications/NotificationForm";
import IntegrationsTab from "../settings/IntegrationsTab";

const getDrawerContent = (
  drawerContent: string,
  activeBrandID: string,
  setDrawerContent: (drawerContent: string | null) => void
) => {
  switch (drawerContent) {
    case BUILD_NAVIGATION:
      return <NavigationTab brandID={activeBrandID} />;
    case BUILD_THEME:
      return <ThemeTab brandID={activeBrandID} />;
    case ENGAGE_FEED_CREATE:
      return (
        <Container maxWidth="md">
          <FeedPostForm
            onBackNavigateOverride={() => setDrawerContent(null)}
            onUpdateComplete={() => setDrawerContent(null)}
            brandID={activeBrandID}
          />
        </Container>
      );
    case ENGAGE_NOTIFICATIONS:
      return (
        <Container maxWidth="md">
          <NotificationForm
            brandID={activeBrandID}
            onUpdateComplete={() => setDrawerContent(null)}
            onBackNavigateOverride={() => setDrawerContent(null)}
          />
        </Container>
      );
    case SETTINGS_INTEGRATIONS:
      return <IntegrationsTab brandID={activeBrandID} />;
    default:
      return null;
  }
};

const getDrawerHeader = (drawerContent: string) => {
  switch (drawerContent) {
    case BUILD_NAVIGATION:
      return "Update Navigation";
    case BUILD_THEME:
      return "Update Theme";
    case ENGAGE_FEED_CREATE:
      return "Create Feed Post";
    case ENGAGE_NOTIFICATIONS:
      return "Create Push Notification";
    case SETTINGS_INTEGRATIONS:
      return "Update Integrations";
  }
};

const GetStartedTab2BottomSheet = ({
  drawerContent,
  setDrawerContent,
}: {
  drawerContent: string | null;
  setDrawerContent: (drawerContent: string | null) => void;
}) => {
  const currentUserContext = useContext(CurrentUserContext);
  const activeBrandID = nullthrows(currentUserContext?.activeBrandID);

  const dispatch = useMobilePreviewDispatch();

  useEffect(() => {
    if (drawerContent === null) {
      return;
    }

    switch (drawerContent) {
      case BUILD_NAVIGATION:
      case BUILD_THEME:
        dispatch({
          type: ReducerAction.SET_PREVIEW_TYPE,
          payload: {
            previewType: PreviewType.NAVIGATION,
          },
        });
        break;
      case ENGAGE_FEED_CREATE:
        dispatch({
          type: ReducerAction.SET_PREVIEW_TYPE,
          payload: {
            previewType: PreviewType.SINGLE_POST,
          },
        });
        break;
      case ENGAGE_NOTIFICATIONS:
        dispatch({
          type: ReducerAction.SET_PREVIEW_TYPE,
          payload: {
            previewType: PreviewType.NOTIFICATION,
          },
        });
        break;
    }

    return () => {
      dispatch({ type: ReducerAction.RESET });
    };
  }, [drawerContent, dispatch]);

  const shouldShowPreview =
    drawerContent === BUILD_NAVIGATION ||
    drawerContent === BUILD_THEME ||
    drawerContent === ENGAGE_FEED_CREATE ||
    drawerContent === ENGAGE_NOTIFICATIONS;

  return (
    <Drawer
      anchor="bottom"
      open={drawerContent !== null}
      onClose={() => setDrawerContent(null)}
      PaperProps={{
        sx: {
          height: "90vh",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
        },
      }}
    >
      <Stack
        direction="column"
        sx={{
          height: "100%",
          overflow: "hidden", // Prevent outer stack from scrolling
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={2}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0, // Prevent header from shrinking
          }}
        >
          <Typography variant="h6">
            {getDrawerHeader(drawerContent ?? "")}
          </Typography>
          <IconButton onClick={() => setDrawerContent(null)}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            height: "100%",
            overflow: "hidden", // Prevent horizontal stack from scrolling
          }}
        >
          <Box
            sx={{
              overflow: "auto", // Only this box should scroll
              flex: 1,
              height: "100%",
              py: 4,
            }}
          >
            <Suspense
              fallback={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              {getDrawerContent(
                drawerContent ?? "",
                activeBrandID,
                setDrawerContent
              )}
            </Suspense>
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            {shouldShowPreview && (
              <Suspense fallback={null}>
                <MobilePreview forceShow={true} />
              </Suspense>
            )}
          </Box>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default GetStartedTab2BottomSheet;
